import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useBankProfile } from '../contexts/BankProfileContext';
import { useAuth } from './useAuth';

export const MESSAGES_PAGE_SIZE = 15;

export interface IMessage {
	created_at: string;
	last_message_at: string;
	tags: string[];
	topic: {
		name: string;
		value: string;
	};
	unread_count: number;
	updated_at: string;
	uuid: string;
	author: string;
	customer_id: string;
}

interface PaginationMeta {
	count: number;
	current_page: number;
	has_next_page: boolean;
	limit: number;
	offset: number;
}

interface PaginatedMessages {
	items: IMessage[];
	pagination: PaginationMeta;
}

export const useMessages = (currentPage: number, limit = MESSAGES_PAGE_SIZE) => {
	const { selectedProfile } = useBankProfile();
	const { session } = useAuth();

	const offset = (currentPage - 1) * limit;

	const getMessages = async (): Promise<PaginatedMessages> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile.id}/threads`,
			{
				params: {
					page: currentPage,
					offset,
					limit,
					'filter[include_unread_count]': true,
				},
				headers: {
					Authorization: session?.tokenized,
				},
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['threads', selectedProfile?.id, currentPage, limit, session?.tokenized],
		queryFn: getMessages,
		enabled: !!selectedProfile && !!session,
		select: (data) => ({
			items: data.items ?? [],
			pagination: data.pagination,
		}),
	});
};

export default useMessages;
